import type { UserResponse } from '@malaberg/shared';
/**
 * EditUserModal — T27.
 *
 * Allows admins to change user name, role, and status.
 * Admin cannot change to/from system_owner (option disabled for them).
 */
import { Button, Group, Modal, Select, Stack, TextInput } from '@mantine/core';
import { useState } from 'react';
import { usersApi } from '../../api/endpoints/users';
import { useAuthStore } from '../../state/auth-store';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetUser: UserResponse;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EditUserModal({ opened, onClose, onSuccess, targetUser }: Props) {
  const currentUser = useAuthStore((s) => s.user);

  const [name, setName] = useState(targetUser.name);
  const [role, setRole] = useState(targetUser.role);
  const [status, setStatus] = useState(targetUser.status);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = currentUser?.role === 'admin';

  const roleOptions = [
    { value: 'writer', label: 'writer' },
    {
      value: 'admin',
      label: 'admin',
      // Admin cannot grant or revoke system_owner; they can freely toggle writer↔admin
      disabled: false,
    },
    {
      value: 'system_owner',
      label: 'system_owner',
      // Admin cannot move to/from system_owner — only system_owner can
      disabled: isAdmin,
    },
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await usersApi.update(targetUser.id, { name, role, status });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit user — ${targetUser.email}`}
      centered
      data-testid="edit-user-modal"
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          <TextInput
            data-testid="edit-name"
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />

          <Select
            data-testid="edit-role"
            label="Role"
            data={roleOptions}
            value={role}
            onChange={(val) => setRole(val as typeof role)}
          />

          <Select
            data-testid="edit-status"
            label="Status"
            data={statusOptions}
            value={status}
            onChange={(val) => setStatus(val as typeof status)}
          />

          {error && (
            <div data-testid="edit-error" style={{ color: 'red' }}>
              {error}
            </div>
          )}

          <Group justify="flex-end">
            <Button data-testid="edit-cancel" variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button data-testid="edit-submit" type="submit" loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
